import { useEffect, useRef, useState } from 'react';
import { ModalTitle } from '@overlayqa/shared/ui';
import { Settings02Icon } from '@hugeicons/core-free-icons';
import { useModalA11y } from '@/hooks/useModalA11y';
import { ModalPortal } from '@/components/shared/ModalPortal';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { LoadError } from '@/components/shared/IssuesLoadError';
import { useProject } from '../../../hooks/useProjects';
import { GeneralTab } from './GeneralTab';
import { SharingTab } from './SharingTab';
import { AuditsTab } from './AuditsTab';
import { DangerZoneTab } from './DangerZoneTab';
import {
  SETTINGS_TAB_LABELS,
  resolveSettingsTab,
  visibleSettingsTabs,
  type SettingsTab,
} from './projectSettingsTabs';

interface ProjectSettingsModalProps {
  projectId: string;
  /** A requested tab (e.g. `?settings=audits`); anything the caller cannot see opens General. */
  initialTab?: string | null;
  onClose: () => void;
  /** Reports the tab the user moved to, so a host can keep its URL in step. */
  onTabChange?: (tab: SettingsTab) => void;
}

/**
 * The one Project Settings surface (Emily, 2026-09-17: "Edit Project and
 * project settings should be merged lets use the modal for settings", modelled
 * on Pastel's tabbed Canvas Settings).
 *
 * Before this, "project settings" named two screens: the Projects-page Edit
 * modal and the /projects/:id/settings route. Incident 2026-08-11: a user sent
 * to one looked for a field that lived on the other and churned ("There is no
 * button for it"). Both doors (the project card menu and the issues-page gear)
 * now open this modal, and the old route redirects into it.
 */
export function ProjectSettingsModal({ projectId, initialTab, onClose, onTabChange }: ProjectSettingsModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalA11y(true, dialogRef, onClose);
  const { data: project, isLoading, error, refetch } = useProject(projectId);
  const role = project?.role;
  const tabs = project ? visibleSettingsTabs(role) : [];
  const [tab, setTab] = useState<SettingsTab | null>(null);
  const activeTab: SettingsTab = tab ?? resolveSettingsTab(initialTab, role);

  // A viewer has nothing to change here; both doors are closed to them, so this
  // only fires for a hand-typed deep link.
  const closedForRole = !!project && tabs.length === 0;
  useEffect(() => {
    if (closedForRole) onClose();
  }, [closedForRole, onClose]);

  const selectTab = (next: SettingsTab) => {
    setTab(next);
    onTabChange?.(next);
  };

  return (
    <ModalPortal>
      <div
        ref={dialogRef}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="project-settings-title"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        {/* id kept from the retired settings page: the project-detail journey
            and three verify scripts anchor on it. */}
        <div id="project-settings" className="bg-white rounded-xl w-full max-w-2xl shadow-xl overflow-hidden flex flex-col max-h-[calc(100vh-2rem)]">
          <div className="px-6 py-2 border-b border-gray-100 flex items-center justify-between">
            <ModalTitle icon={Settings02Icon} id="project-settings-title" className="oq-dialog__title">Project Settings</ModalTitle>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-2"
              aria-label="Close"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          {isLoading && <p className="px-6 py-5 state-note">Loading...</p>}

          {!isLoading && !project && (
            <div className="px-6 py-5">
              <LoadError
                compact
                resource="project settings"
                error={(error as Error) ?? new Error('Project not found')}
                onRetry={() => refetch()}
              />
            </div>
          )}

          {project && tabs.length > 0 && (
            <>
              <SegmentedControl<SettingsTab>
                aria-label="Project settings sections"
                className="mx-6 mt-4"
                value={activeTab}
                onChange={selectTab}
                options={tabs.map((t) => ({ value: t, label: SETTINGS_TAB_LABELS[t] }))}
              />
              <div className="overflow-y-auto">
                {activeTab === 'general' && <GeneralTab project={project} onClose={onClose} />}
                {activeTab === 'sharing' && <SharingTab project={project} />}
                {activeTab === 'audits' && <AuditsTab project={project} />}
                {activeTab === 'danger' && <DangerZoneTab project={project} onClose={onClose} />}
              </div>
            </>
          )}
        </div>
      </div>
    </ModalPortal>
  );
}
